import { useEffect, useState } from 'react';
import { useScrollReveal, animStyle } from '../../../hooks/useScrollReveal';

interface Stat {
  value: number;
  suffix: string;
  label: string;
  desc: string;
  icon: string;
}

const stats: Stat[] = [
  { value: 16, suffix: '年+', label: 'デジタルマーケティング経験', desc: '代表の豊富な業界キャリア', icon: 'ri-medal-line' },
  { value: 100, suffix: '+',  label: '運用アカウント実績',            desc: '大手〜中堅企業まで幅広く',  icon: 'ri-building-2-line' },
  { value: 7,  suffix: '',   label: '対応SNSプラットフォーム',      desc: '主要SNS全てをカバー',       icon: 'ri-global-line' },
  { value: 4,  suffix: '',   label: 'ワンストップサービス',          desc: '必要な施策をまとめて提供',  icon: 'ri-service-line' },
];

interface StatItemProps extends Stat {
  visible: boolean;
  delay: number;
  isLast: boolean;
}

function StatItem({ value, suffix, label, desc, icon, visible, delay, isLast }: StatItemProps) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!visible || started) return;
    const timer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(timer);
  }, [visible, started, delay]);

  useEffect(() => {
    if (!started) return;
    let startTime: number;
    const duration = 1800;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(value * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, value]);

  return (
    <div
      className={`flex flex-col items-center text-center px-4 sm:px-6 py-8 md:py-10 group ${!isLast ? 'border-r border-white/10' : ''}`}
      style={animStyle(visible, 'up', delay, 700)}
    >
      <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border border-white/20 mb-4 md:mb-5 group-hover:border-white/50 transition-colors duration-300">
        <i className={`${icon} text-base md:text-lg text-white/60 group-hover:text-white/90 transition-colors duration-300`} />
      </div>
      <div className="text-4xl sm:text-5xl md:text-6xl font-light text-white tracking-tight font-['Montserrat'] mb-1.5 md:mb-2 leading-none">
        {count}
        <span className="text-2xl sm:text-3xl md:text-4xl text-white/70">{suffix}</span>
      </div>
      <p className="text-white/80 text-[10px] sm:text-xs tracking-widest uppercase mb-1 md:mb-1.5 font-['Montserrat'] leading-snug">{label}</p>
      <p className="text-white/40 text-[10px] sm:text-xs leading-relaxed hidden sm:block">{desc}</p>
    </div>
  );
}

export default function Numbers() {
  const { ref, visible } = useScrollReveal(0.25);

  return (
    <section className="w-full bg-[#0d0d0d] border-y border-white/8">
      <div ref={ref} className="w-full grid grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, i) => (
          <StatItem
            key={i}
            {...stat}
            visible={visible}
            delay={i * 150}
            isLast={i === stats.length - 1}
          />
        ))}
      </div>
    </section>
  );
}
